import React, { useState } from "react"

import { Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { isHivedEmail } from "@/constants/hived"
import { ALL_LOCATIONS, ALL_SHERIFF_AREAS } from "@/constants/sheriffOptions"
import { useCreateEmployee } from "@/hooks/useCreateEmployee"
import { useSubTeams } from "@/hooks/useSubTeams"

interface CreateEmployeeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const NO_TEAM = "__none__"

const functionAreaOptions = ALL_SHERIFF_AREAS.map((a) => ({ value: a, label: a }))
const locationOptions = ALL_LOCATIONS.map((loc) => ({ value: loc, label: loc }))

const emptyForm = {
  firstName: "",
  surname: "",
  email: "",
  functionAreas: [] as string[],
  location: [] as string[],
  driverManagerTeam: "",
}

const CreateEmployeeDialog: React.FC<CreateEmployeeDialogProps> = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(emptyForm)
  const { subTeamOptionsWithIds } = useSubTeams()
  const { mutateAsync: createEmployee, isPending } = useCreateEmployee()

  const driverManagerTeamOptions = subTeamOptionsWithIds
    .filter((t) => t.type === "DRIVER_MANAGER_TEAM")
    .map((t) => ({ value: t.value, label: t.label }))

  const canSubmit =
    form.firstName.trim().length > 0 &&
    form.surname.trim().length > 0 &&
    form.email.trim().length > 0

  const resetAndClose = () => {
    setForm(emptyForm)
    onOpenChange(false)
  }

  const handleSubmit = async () => {
    if (!canSubmit || isPending) return

    if (!isHivedEmail(form.email)) {
      toast.error(`Email domain must be HIVED`)
      return
    }

    try {
      const result = await createEmployee({
        firstName: form.firstName.trim(),
        surname: form.surname.trim(),
        email: form.email.trim(),
        functionAreas: form.functionAreas,
        location: form.location,
        driverManagerTeam: form.driverManagerTeam || undefined,
      })

      toast.success(
        `Created ${result.person.firstName} ${result.person.lastName} (ID ${result.person.personId})`,
      )
      resetAndClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create employee")
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : resetAndClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create HIVED employee</DialogTitle>
          <DialogDescription>
            Creates a HIVED person with a generated person ID. No DriverHub invite is sent.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="create-employee-first-name">First name</Label>
              <Input
                id="create-employee-first-name"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                placeholder="First name"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="create-employee-surname">Surname</Label>
              <Input
                id="create-employee-surname"
                value={form.surname}
                onChange={(e) => setForm((f) => ({ ...f, surname: e.target.value }))}
                placeholder="Surname"
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="create-employee-email">Email</Label>
            <Input
              id="create-employee-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="name@hived.space"
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Function areas</Label>
            <MultiSelect
              options={functionAreaOptions}
              selected={form.functionAreas}
              onChange={(functionAreas) => setForm((f) => ({ ...f, functionAreas }))}
              placeholder="Select function areas"
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Locations</Label>
            <MultiSelect
              options={locationOptions}
              selected={form.location}
              onChange={(location) => setForm((f) => ({ ...f, location }))}
              placeholder="Select locations"
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Driver manager team</Label>
            <Select
              value={form.driverManagerTeam || NO_TEAM}
              onValueChange={(value) =>
                setForm((f) => ({ ...f, driverManagerTeam: value === NO_TEAM ? "" : value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TEAM}>None</SelectItem>
                {driverManagerTeamOptions.map((team) => (
                  <SelectItem key={team.value} value={team.value}>
                    {team.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={resetAndClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create employee
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CreateEmployeeDialog
